import { useEffect, useRef, useState } from "react";
import type { ProductData } from "@/mocks/products";

interface Props {
  product: ProductData;
}

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function ProductEssence({ product }: Props) {
  const { ref, inView } = useInView(0.1);

  return (
    <section className="relative bg-[#060508] py-40 px-6 overflow-hidden">
      {/* Subtle glow */}
      <div
        className="absolute top-1/2 left-0 w-[500px] h-[500px] rounded-full pointer-events-none -translate-y-1/2"
        style={{
          background: `radial-gradient(circle, ${product.glowColor} 0%, transparent 70%)`,
          filter: "blur(80px)",
        }}
      />

      <div ref={ref} className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          {/* Image */}
          <div
            className="relative overflow-hidden"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateX(0)" : "translateX(-30px)",
              transition: "opacity 1.8s ease, transform 1.8s ease",
            }}
          >
            <div className="relative" style={{ paddingBottom: "75%" }}>
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${product.essenceImage})`,
                  filter: "brightness(0.55) saturate(0.7)",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#060508]/60 via-transparent to-transparent" />
              {/* Glowing border */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  boxShadow: `inset 0 0 60px ${product.glowColor}`,
                  border: `1px solid ${product.accentColor}15`,
                }}
              />
            </div>
          </div>

          {/* Text */}
          <div
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? "translateX(0)" : "translateX(30px)",
              transition: "opacity 1.8s ease 0.3s, transform 1.8s ease 0.3s",
            }}
          >
            <div
              className="text-xs tracking-[0.5em] uppercase mb-8"
              style={{ color: `${product.accentColor}50` }}
            >
              Essence
            </div>
            <h2
              className="text-3xl md:text-4xl font-light text-white/80 mb-12 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.04em" }}
            >
              {product.essenceTitle}
            </h2>

            <div className="space-y-4">
              {product.essenceLines.map((line, i) => (
                <p
                  key={i}
                  className="text-white/40 text-lg font-light leading-relaxed"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    letterSpacing: "0.05em",
                    opacity: inView ? 0.4 : 0,
                    transition: `opacity 2s ease ${0.5 + i * 0.2}s`,
                  }}
                >
                  {line}
                </p>
              ))}
            </div>

            {/* Accent line */}
            <div
              className="mt-12 w-16 h-px"
              style={{ background: `${product.accentColor}30` }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
